import React, { useEffect, useState } from "react";
import { api, type Me } from "./api";
import { ReviewPanel, type Note } from "./ReviewPanel";
import { AllergyBanner, type AllergyBannerData } from "./AllergyBanner";
import { FLAG_LABEL, REASON_LABEL, REVIEW_LABEL, HoldBadge } from "./ReviewQueue";
import { DocRequestsBox, HoldBox, type DocRequest, type ComposerOptions, type HoldOptions } from "./RecordFollowUp";

/**
 * The review record (build plan §7.1 #3): Step 3 and Step 4 of the family's
 * live application, read-only, with the allergy banner first and the medical
 * documents streamed through the server. Each open is audited on the server.
 */

type Answer = "Yes" | "No" | null;
type Permission = "yes" | "no" | "call";

export interface Person {
  personKey: string;
  lastName: string;
  firstName: string;
  dateOfBirth: string;
  ageAtProgram: string;
  turns18On: string;
  city: string;
  reviewStatus: string;
  reviewedByEmail: string;
  reviewedAtUtc: string;
  sourceLastUpdatedUtc: string;
  flags: { type: string; reasons?: string[]; [k: string]: unknown }[];
  expectedReasons: string[];
  familyUpdatedAfterReview: boolean;
  attendanceHold: string;
  attendanceHoldDecidedByEmail: string;
  attendanceHoldDecidedAtUtc: string;
  holdRequested: boolean;
}

interface Doc {
  fieldKey: string;
  label: string;
  uploaded: boolean;
  filename: string;
  contentType: string;
  isImage: boolean;
}

export interface RecordViewData {
  identity: { firstName: string; lastName: string; preferredName: string; dateOfBirth: string; ageAtProgram: string; referenceNumber: string; status: string };
  conditions: { id: string; label: string; answer: Answer; explanation: string; extras: { label: string; value: string }[] }[];
  diseases: { key: string; label: string; answer: Answer }[];
  accommodations: { answer: Answer; description: string };
  healthChanges: { answer: Answer; description: string };
  immunizations: { upToDate: Answer; comments: string; lastPhysicalDate: string };
  primaryCare: { name: string; phone: string; address: string };
  medications: { answer: Answer; rows: { name: string; strength: string; dose: string; form: string; times: string }[] };
  selfCarry: { answer: Answer; types: string[] };
  otc: { listVersion: string; items: { id: string; name: string; use: string; permission: Permission }[] };
  topicals: { key: string; label: string; brand: string; give: Permission; assist: Permission }[];
  medicationAllergyFollowUp: { answer: Answer; detail: string };
  insurance: { carrierName: string; policyId: string; groupNumber: string; policyholderName: string; policyholderRelationship: string; policyholderDob: string; claimsPhone: string };
  documents: Doc[];
}

export interface RecordResponse {
  person: Person;
  notes: Note[];
  docRequests: DocRequest[];
  application: RecordViewData | null;
  allergyBanner: AllergyBannerData;
  composer: ComposerOptions;
  hold: HoldOptions;
}

export function eastern(iso: string): string {
  if (!iso) return "";
  const t = new Date(iso);
  if (Number.isNaN(t.getTime())) return "";
  return new Intl.DateTimeFormat("en-US", { timeZone: "America/New_York", month: "short", day: "numeric", year: "numeric", hour: "numeric", minute: "2-digit" }).format(t);
}

/** ✅ / ❌ / ☎ with words, so it reads without the symbol too. */
function Perm({ p }: { p: Permission }) {
  if (p === "yes") return <span><span aria-hidden="true">✅</span> Yes</span>;
  if (p === "call") return <span className="font-medium"><span aria-hidden="true">☎</span> Call first</span>;
  return <span className="text-slate-600"><span aria-hidden="true">❌</span> No</span>;
}

function Ans({ a }: { a: Answer }) {
  if (a === "Yes") return <span className="font-semibold">Yes</span>;
  if (a === "No") return <span className="text-slate-600">No</span>;
  return <span className="text-amber-800">Not answered</span>;
}

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <section className="section-card">
      <h3 className="mb-3 text-base font-semibold">{title}</h3>
      {children}
    </section>
  );
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="min-w-0">
      <dt className="text-xs text-slate-500">{label}</dt>
      <dd className="break-words text-sm">{children || <span className="text-slate-400">—</span>}</dd>
    </div>
  );
}

/** One medical document: fetched once (one audited view), shown from memory, never a stored URL. */
function DocumentViewer({ personKey, doc, onClose }: { personKey: string; doc: Doc; onClose: () => void }) {
  const [state, setState] = useState<{ url: string; type: string } | { error: string } | null>(null);
  useEffect(() => {
    let url: string | null = null;
    let cancelled = false;
    void (async () => {
      try {
        const res = await fetch(`/api/records/${encodeURIComponent(personKey)}/documents/${encodeURIComponent(doc.fieldKey)}`, { credentials: "same-origin" });
        if (!res.ok) {
          const body = await res.json().catch(() => ({}));
          if (!cancelled) setState({ error: (body as { error?: string }).error ?? "This document can't be shown." });
          return;
        }
        const blob = await res.blob();
        url = URL.createObjectURL(blob);
        if (!cancelled) setState({ url, type: blob.type });
      } catch {
        if (!cancelled) setState({ error: "Can't reach the server. Check the connection." });
      }
    })();
    return () => {
      cancelled = true;
      if (url) URL.revokeObjectURL(url);
    };
  }, [personKey, doc.fieldKey]);

  return (
    <div className="mt-3 rounded-md border border-slate-300 bg-slate-50 p-3">
      <div className="mb-2 flex flex-wrap items-center justify-between gap-2">
        <p className="min-w-0 break-words text-sm font-medium">
          {doc.label} · <span className="font-normal">{doc.filename}</span>
        </p>
        <div className="flex gap-2">
          {state && "url" in state && (
            <a className="btn-secondary" href={state.url} target="_blank" rel="noopener noreferrer">
              Open full size
            </a>
          )}
          <button type="button" className="btn-secondary" onClick={onClose}>Close</button>
        </div>
      </div>
      {!state && <p className="text-sm text-slate-600">Loading…</p>}
      {state && "error" in state && <p className="field-error" role="alert">{state.error}</p>}
      {state && "url" in state &&
        (state.type.startsWith("image/") ? (
          <img src={state.url} alt={doc.label} className="max-h-[70vh] max-w-full rounded border border-slate-200 bg-white object-contain" />
        ) : (
          <iframe src={state.url} title={doc.label} className="h-[70vh] w-full rounded border border-slate-200 bg-white" />
        ))}
    </div>
  );
}

export function Documents({ personKey, docs }: { personKey: string; docs: Doc[] }) {
  const [open, setOpen] = useState<string | null>(null);
  return (
    <Section title="Medical documents">
      <ul className="divide-y divide-slate-100">
        {docs.map((d) => (
          <li key={d.fieldKey} className="py-2">
            <div className="flex flex-wrap items-center justify-between gap-2">
              <span className="min-w-0 text-sm">
                <span className="font-medium">{d.label}</span>
                {d.uploaded ? <span className="ml-2 break-all text-slate-600">{d.filename}</span> : <span className="ml-2 text-amber-800">Not uploaded</span>}
              </span>
              {d.uploaded && (
                <button type="button" className="btn-secondary" onClick={() => setOpen(open === d.fieldKey ? null : d.fieldKey)} aria-expanded={open === d.fieldKey}>
                  {open === d.fieldKey ? "Hide" : "View"}
                </button>
              )}
            </div>
            {open === d.fieldKey && <DocumentViewer personKey={personKey} doc={d} onClose={() => setOpen(null)} />}
          </li>
        ))}
      </ul>
    </Section>
  );
}

export function ApplicationSections({ personKey, a }: { personKey: string; a: RecordViewData }): React.ReactElement {
  const yesConditions = a.conditions.filter((c) => c.answer === "Yes");
  const unanswered = a.conditions.filter((c) => c.answer === null);
  return (
    <>
      <Documents personKey={personKey} docs={a.documents} />

      <Section title="Medications">
        <p className="mb-2 text-sm">Takes medication: <Ans a={a.medications.answer} /></p>
        {a.medications.rows.length > 0 && (
          <div className="overflow-x-auto">
            <table className="w-full text-left text-sm">
              <thead className="border-b border-slate-200 text-slate-500">
                <tr>
                  <th className="py-1 pr-3 font-medium">Medication</th>
                  <th className="py-1 pr-3 font-medium">Strength</th>
                  <th className="py-1 pr-3 font-medium">Dose</th>
                  <th className="py-1 font-medium">When</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {a.medications.rows.map((m, i) => (
                  <tr key={i}>
                    <td className="py-1 pr-3 font-medium">{m.name}</td>
                    <td className="py-1 pr-3">{m.strength}</td>
                    <td className="py-1 pr-3">{[m.dose, m.form].filter(Boolean).join(" ")}</td>
                    <td className="py-1">{m.times}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
        <p className="mt-3 text-sm">
          Self-carries emergency medication: <Ans a={a.selfCarry.answer} />
          {a.selfCarry.types.length > 0 && <> — {a.selfCarry.types.join(", ")}</>}
        </p>
      </Section>

      <Section title="Health conditions">
        {yesConditions.length === 0 ? (
          <p className="text-sm text-slate-700">No conditions answered Yes.</p>
        ) : (
          <ul className="space-y-2">
            {yesConditions.map((c) => (
              <li key={c.id} className="text-sm">
                <p className="font-semibold">{c.label}</p>
                {c.explanation && <p className="whitespace-pre-wrap">{c.explanation}</p>}
                {c.extras.map((x) => (
                  <p key={x.label} className="text-slate-700">{x.label}: {x.value}</p>
                ))}
              </li>
            ))}
          </ul>
        )}
        {unanswered.length > 0 && (
          <p className="mt-2 text-sm text-amber-800">Not answered: {unanswered.map((c) => c.label).join("; ")}</p>
        )}
        <details className="mt-3 text-sm">
          <summary className="cursor-pointer text-slate-600">Every condition ({a.conditions.length})</summary>
          <ul className="mt-2 grid gap-x-6 gap-y-1 sm:grid-cols-2">
            {a.conditions.map((c) => (
              <li key={c.id} className="flex justify-between gap-3 border-b border-slate-100 py-0.5">
                <span className="min-w-0">{c.label}</span>
                <Ans a={c.answer} />
              </li>
            ))}
          </ul>
        </details>
        <h4 className="mb-1 mt-4 text-sm font-semibold">Diseases had</h4>
        <ul className="flex flex-wrap gap-x-6 gap-y-1 text-sm">
          {a.diseases.map((d) => (
            <li key={d.key}>{d.label}: <Ans a={d.answer} /></li>
          ))}
        </ul>
      </Section>

      <Section title="Accommodations and changes since the physical">
        <dl className="grid gap-3 sm:grid-cols-2">
          <Field label="Accommodation requested"><Ans a={a.accommodations.answer} />{a.accommodations.description && <span className="block whitespace-pre-wrap">{a.accommodations.description}</span>}</Field>
          <Field label="Health changes since the physical"><Ans a={a.healthChanges.answer} />{a.healthChanges.description && <span className="block whitespace-pre-wrap">{a.healthChanges.description}</span>}</Field>
        </dl>
      </Section>

      <Section title="Over-the-counter medication permissions">
        <ul className="grid gap-x-6 gap-y-1 text-sm sm:grid-cols-2">
          {a.otc.items.map((o) => (
            <li key={o.id} className="flex justify-between gap-3 border-b border-slate-100 py-0.5">
              <span className="min-w-0">{o.name}</span>
              <Perm p={o.permission} />
            </li>
          ))}
        </ul>
        <p className="mt-3 text-sm">
          Allergies to medications (follow-up): <Ans a={a.medicationAllergyFollowUp.answer} />
          {a.medicationAllergyFollowUp.detail && <> — {a.medicationAllergyFollowUp.detail}</>}
        </p>
        {a.otc.listVersion && <p className="field-hint mt-1">OTC list version {a.otc.listVersion}</p>}
      </Section>

      <Section title="Sunscreen and insect repellent">
        <ul className="space-y-2 text-sm">
          {a.topicals.map((t) => (
            <li key={t.key}>
              <p className="font-medium">{t.label} <span className="font-normal text-slate-600">({t.brand})</span></p>
              <p>Give/apply: <Perm p={t.give} /> · Assist: <Perm p={t.assist} /></p>
            </li>
          ))}
        </ul>
      </Section>

      <Section title="Immunizations and primary care">
        <dl className="grid gap-3 sm:grid-cols-3">
          <Field label="Vaccines up to date"><Ans a={a.immunizations.upToDate} /></Field>
          <Field label="Comments">{a.immunizations.comments}</Field>
          <Field label="Last physical">{a.immunizations.lastPhysicalDate}</Field>
          <Field label="Primary care provider">{a.primaryCare.name}</Field>
          <Field label="Phone">{a.primaryCare.phone}</Field>
          <Field label="Address">{a.primaryCare.address}</Field>
        </dl>
      </Section>

      <Section title="Insurance (Step 4)">
        <dl className="grid gap-3 sm:grid-cols-3">
          <Field label="Carrier">{a.insurance.carrierName}</Field>
          <Field label="Policy / member ID">{a.insurance.policyId}</Field>
          <Field label="Group number">{a.insurance.groupNumber}</Field>
          <Field label="Policyholder">{a.insurance.policyholderName}</Field>
          <Field label="Relationship">{a.insurance.policyholderRelationship}</Field>
          <Field label="Policyholder date of birth">{a.insurance.policyholderDob}</Field>
          <Field label="Claims phone">{a.insurance.claimsPhone}</Field>
        </dl>
      </Section>
    </>
  );
}

export function PersonHeader({ p }: { p: Person }): React.ReactElement {
  const flags = p.flags.filter((f) => f.type !== "ExpectedAtMedStation" && f.type !== "FamilyUpdatedAfterReview");
  return (
    <div className="section-card">
      <div className="flex flex-wrap items-baseline justify-between gap-2">
        <h2 className="text-xl font-semibold">{p.lastName}, {p.firstName}</h2>
        <span className="text-sm text-slate-600">{p.personKey}</span>
      </div>
      <dl className="mt-2 grid gap-3 sm:grid-cols-4">
        <Field label="Date of birth">{p.dateOfBirth}</Field>
        <Field label="Age at program">{p.ageAtProgram}</Field>
        <Field label="City">{p.city || "Not assigned"}</Field>
        <Field label="Review">
          {REVIEW_LABEL[p.reviewStatus] ?? p.reviewStatus}
          {p.reviewedAtUtc && <span className="block text-xs text-slate-500">last by {p.reviewedByEmail} · {eastern(p.reviewedAtUtc)}</span>}
        </Field>
      </dl>
      {(p.familyUpdatedAfterReview || flags.length > 0 || p.expectedReasons.length > 0 || p.turns18On || (p.attendanceHold && p.attendanceHold !== "None")) && (
        <div className="mt-3 flex flex-wrap gap-2 text-xs">
          <HoldBadge hold={p.attendanceHold} />
          {p.familyUpdatedAfterReview && <span className="rounded bg-amber-100 px-2 py-0.5 font-medium text-amber-900">Family updated after review</span>}
          {flags.map((f) => (
            <span key={f.type} className="rounded bg-red-50 px-2 py-0.5 text-red-900">{FLAG_LABEL[f.type] ?? f.type}</span>
          ))}
          {p.expectedReasons.length > 0 && (
            <span className="rounded bg-sky-50 px-2 py-0.5 text-sky-900">Med station: {p.expectedReasons.map((r) => REASON_LABEL[r] ?? r).join(", ")}</span>
          )}
          {p.turns18On && <span className="rounded bg-slate-100 px-2 py-0.5 text-slate-800">Turns 18 on {p.turns18On}</span>}
        </div>
      )}
    </div>
  );
}

export function RecordScreen({ personKey, me }: { personKey: string; me: Me }): React.ReactElement {
  const [data, setData] = useState<RecordResponse | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    void api<RecordResponse>("GET", `/api/records/${encodeURIComponent(personKey)}`).then((r) => {
      if (r.ok) setData(r.data);
      else setError(r.data.error ?? "Could not load this record.");
    });
  }, [personKey]);

  return (
    <div>
      <p className="mb-3 text-sm"><a href="/review" className="text-program underline">← Record review</a></p>
      {error && <div className="section-card" role="alert"><p className="text-sm text-red-700">{error}</p></div>}
      {!data && !error && <p className="text-sm text-slate-600">Loading…</p>}
      {data && (
        <>
          <AllergyBanner data={data.allergyBanner} />
          <PersonHeader p={data.person} />
          <ReviewPanel
            me={me}
            person={data.person}
            notes={data.notes}
            onChange={(r) => setData({ ...data, person: r.person, notes: r.notes, docRequests: r.docRequests ?? data.docRequests })}
            middle={
              <>
                <HoldBox me={me} person={data.person} options={data.hold} onDone={(r) => setData({ ...data, ...r })} />
                <DocRequestsBox me={me} person={data.person} requests={data.docRequests} options={data.composer} onDone={(r) => setData({ ...data, ...r })} />
              </>
            }
          />
          {data.application ? (
            <ApplicationSections personKey={personKey} a={data.application} />
          ) : (
            <div className="section-card" role="note">
              <p className="text-sm text-amber-900">The application for this person could not be found in the applications list. It may have been withdrawn.</p>
            </div>
          )}
        </>
      )}
    </div>
  );
}
